import React, { useEffect, useState, useRef, useCallback } from "react";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../components/CustomModel/CustomModal";
import PlayerForm from "../../players/PlayerForm";
import { COUNTRY_OPTIONS, LEVEL_OPTIONS } from "../../../constants";
import { getOptionName } from "../../../utils/getOptionName";
import { toast } from "react-toastify";
import useSubmitLock from "../../../hooks/useSubmitLock";

const TournamentPlayerForm = ({ tournamentId, playerBase, rating, sorting, oldPlayerId, teamNumber, teamId, onClose, onSuccess, canWrite }) => {
    const api = useAxios();
    const { t } = useTranslation();
    // The result list stays mounted and clickable for the whole round-trip
    // (onClose only runs after the await), so a second tap while the first
    // POST was in flight used to fire a SECOND create for the same
    // team_id + sorting. The backend happily creates both rows - there's no
    // (team, sorting) uniqueness and no team-size limit - which is how live
    // doubles tournaments ended up with pairs of three, invisible in this tab
    // but showing up in Рейтинг/групах as a third participant. See
    // src/utils/normalizeTeamPlayers.js and
    // backend-tasks/2026-10-20-pair-with-three-participants.md.
    const { withLock, isSubmitting } = useSubmitLock();

    const [players, setPlayers] = useState([]);
    const [query, setQuery] = useState("");
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        if (query.trim().length >= 2) {
            setPlayers([]);
            setOffset(0);
            setHasMore(true);
            searchPlayers(0, true);
        } else {
            setPlayers([]);
        }
    }, [query]);

    useEffect(() => {
        if (offset > 0) {
            searchPlayers(offset, false);
        }
    }, [offset]);

    const searchPlayers = async (currentOffset, isNewSearch) => {
        setLoading(true);
        try {
            // The backend's players search filters on `q`, not `search` -
            // this used to send `search=` (silently ignored server-side, so
            // it returned the base's players unfiltered/empty instead of
            // matching the typed name) until the players view got restored
            // from an older revision that didn't carry this endpoint's own
            // param names along with it.
            const res = await api.get("/api/v1.0/players/?limit=20", {
                params: {
                    q: query,
                    base: playerBase,
                    rating: rating,
                    tournament: tournamentId,
                    offset: currentOffset
                }
            });
            const newPlayers = res.data.results || res.data || [];

            setPlayers(prev => isNewSearch ? newPlayers : [...prev, ...newPlayers]);
            setHasMore(!!res.data.next || newPlayers.length >= 20);
        } catch {
            toast.error(t("Failed to search players"));
        } finally {
            setLoading(false);
        }
    };

    const observer = useRef();
    const lastElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setOffset(prev => prev + 20);
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const handleSelect = (player) => withLock(async () => {
        try {
            let activeTeamId = teamId;
            if (oldPlayerId) {
                await api.patch(
                    `/api/v1.0/tournaments/${tournamentId}/players/${oldPlayerId}/change-player/`,
                    {
                        new_player_id: player.id,
                        ...(teamNumber && { team_number: teamNumber }),
                    }
                );
            } else {
                const res = await api.post(
                    `/api/v1.0/tournaments/${tournamentId}/players/`,
                    {
                        player: player.id,
                        sorting: sorting,
                        ...(teamNumber && { team_number: teamNumber }),
                        ...(teamId && { team_id: teamId }),
                    }
                );
                if (res?.data?.team_id) {
                    activeTeamId = res.data.team_id;
                } else if (res?.data?.team) {
                    activeTeamId = res.data.team;
                }
            }

            // player.rating here is the global search endpoint's own figure -
            // a sum of RatingTournament.score across every tournament in the
            // rating's date window (apps/players/serializers.py get_rating),
            // not this tournament's own per-participant rating value. Passing
            // it straight through showed that cumulative total (often a much
            // bigger number) on the roster card until the next real
            // fetchPlayers() replaced it with the correct tournament-scoped
            // figure - dropping it here instead so the badge just stays
            // hidden until that refresh happens.
            const { rating: _globalRating, ...playerWithoutGlobalRating } = player;
            onSuccess?.({
                ...playerWithoutGlobalRating,
                sorting,
                team_number: teamNumber,
                team_id: activeTeamId,
            });

            onClose();

        } catch {
            toast.error(
                t(oldPlayerId ? "Failed to replace player" : "Failed to add player")
            );
        }
    });


    return (
        <form className="modal-form">
            <div className="modal-sticky-header">
                <h2>
                    <FeatherIcon icon="user" />  {oldPlayerId ? t("Change player") : t("Add player")}
                </h2>
                <div className="form-group" style={{ position: "relative" }}>
                    <ul className="tabs">
                        <li style={{ position: "relative", width: "100%" }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" />
                            <input
                                type="text"
                                className="search"
                                placeholder={t("Search player by first name, last name")}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setOffset(0);
                                }}
                                style={{ paddingLeft: "30px" }}
                                autoFocus
                            />
                        </li>
                        {canWrite &&
                            <li>
                                <a href="#" onClick={(e) => {
                                    e.preventDefault();
                                    setCreateOpen(true);
                                }} className="action-button" style={{ whiteSpace: "nowrap" }}>
                                    <FeatherIcon icon="plus"/>
                                    {t("Create player")}
                                </a>
                            </li>
                        }
                    </ul>
                </div>
            </div>
            {!loading && query.length >= 2 && players.length === 0 && (
                <div className="empty-search">
                    {t("No results found")}
                </div>
            )}

            {players.length > 0 && (
                <ul
                    className="user-search-results players"
                    // Not just an ignored second tap (withLock already drops
                    // it) - the list also has to LOOK unavailable, since the
                    // tap that created the duplicate was the admin reacting to
                    // a modal that still showed a live, tappable list while
                    // the first request was on its way.
                    style={isSubmitting ? { opacity: 0.5, pointerEvents: "none" } : undefined}
                    aria-busy={isSubmitting}
                >
                    {players.map((player, index) => {
                        const isLastItem = players.length === index + 1;
                        return (
                            <li
                                ref={isLastItem ? lastElementRef : null}
                                key={player.id}
                                onClick={() => handleSelect(player)}
                            >
                                <div>
                                    {player.image
                                        ? <img src={player.image} className="thumbnail mini"/>
                                        :  <div className="no-thumbnail mini"><FeatherIcon icon="user"/></div>
                                    }
                                </div>

                                <div>
                                    <span className="uppercase">{player.last_name}</span> {player.first_name}
                                    <br/>
                                    <span className="date">
                                        {player.city}{player.city && player.country && ", "}{t(getOptionName(COUNTRY_OPTIONS, player.country))}
                                    </span>
                                </div>

                                <div className="last" style={{marginLeft: "auto"}}>
                                    {player?.level &&
                                        player.level &&
                                        <span className={`level ${player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}`}>
                                        {t(getOptionName(LEVEL_OPTIONS, player.level))}
                                    </span>
                                    }
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {loading && <div>{t("Searching...")}</div>}

            <CustomModal
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
            >
                <PlayerForm
                    baseId={playerBase}
                    playerId={null}
                    onClose={() => setCreateOpen(false)}
                    onSuccess={(newPlayer) => withLock(async () => {
                        try {
                            let activeTeamId = teamId;
                            if (oldPlayerId) {
                                await api.patch(
                                    `/api/v1.0/tournaments/${tournamentId}/players/${oldPlayerId}/change-player/`,
                                    {
                                        new_player_id: newPlayer.id,
                                        ...(teamNumber && { team_number: teamNumber }),
                                    }
                                );
                            } else {
                                const res = await api.post(
                                    `/api/v1.0/tournaments/${tournamentId}/players/`,
                                    {
                                        player: newPlayer.id,
                                        sorting: sorting,
                                        ...(teamNumber && { team_number: teamNumber }),
                                        ...(teamId && { team_id: teamId }),
                                    }
                                );

                                if (res?.data?.team_id) {
                                    activeTeamId = res.data.team_id;
                                } else if (res?.data?.team) {
                                    activeTeamId = res.data.team;
                                }
                            }
                            onSuccess?.({
                                ...newPlayer,
                                sorting,
                                team_number: teamNumber,
                                team_id: activeTeamId,
                            });
                            setCreateOpen(false);
                            onClose();
                        } catch {
                            toast.error(t(oldPlayerId ? "Failed to replace player" : "Failed to add created player"));
                        }
                    })}
                    canWrite={true}
                />
            </CustomModal>
        </form>
    );
};

export default TournamentPlayerForm;
